import { useEffect, useRef } from 'react';
import { Textarea } from '@/shared/components/TextField';
import FileUpload from '@/shared/components/FileUpload';
import { FileType } from '@/shared/components/FileUpload/constants';
import { RAISE_SERVICE_REQUEST_LABELS as L } from '../../constants/labels';

interface Step2Props {
  description: string;
  descriptionError: string;
  step2ValidationAttempt: number;
  onDescriptionChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;

  images: File[];
  imageUploadProgress: Record<string, number>;
  imagesError: string;
  onImagesSelect: (files: File[]) => void;
  onImagesError: (message: string) => void;

  document: File | null;
  documentUploadProgress: Record<string, number>;
  documentError: string;
  onDocumentSelect: (files: File[]) => void;
  onDocumentError: (message: string) => void;

  isSubmitting: boolean;
}

const Step2DetailsAttachments: React.FC<Step2Props> = ({
  description,
  descriptionError,
  step2ValidationAttempt,
  onDescriptionChange,
  images,
  imageUploadProgress,
  imagesError,
  onImagesSelect,
  onImagesError,
  document,
  documentUploadProgress,
  documentError,
  onDocumentSelect,
  onDocumentError,
  isSubmitting,
}) => {
  const descriptionRef = useRef<HTMLDivElement>(null);
  const imagesRef = useRef<HTMLDivElement>(null);
  const documentRef = useRef<HTMLDivElement>(null);
  const errorsRef = useRef({ descriptionError, imagesError, documentError });
  errorsRef.current = { descriptionError, imagesError, documentError };

  useEffect(() => {
    if (!step2ValidationAttempt) {
      return;
    }
    const {
      descriptionError: de,
      imagesError: ie,
      documentError: doe,
    } = errorsRef.current;
    if (de) {
      descriptionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else if (ie) {
      imagesRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else if (doe) {
      documentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [step2ValidationAttempt]);

  return (
    <div className="flex flex-col gap-8 mt-3">
      {/* Description */}
      <div ref={descriptionRef} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="text-[18px] text-on-surface-variant">
            {L.SECTION_DESCRIPTION}
          </p>
          <p className="text-sm text-[#474A4C]">{L.DESCRIPTION_HELPER}</p>
        </div>
        <Textarea
          fullWidth
          label={L.FIELD_DESCRIPTION_PLACEHOLDER}
          rows={4}
          maxLength={L.DESCRIPTION_MAX_LENGTH}
          value={description}
          onChange={onDescriptionChange}
          error={Boolean(descriptionError)}
          errorMessage={descriptionError}
          disabled={isSubmitting}
        />
      </div>

      <div className="border-b-2 border-[#DCDDE0]" />

      {/* Media and documents */}
      <div className="flex flex-col gap-4">
        <p className="text-[18px] text-on-surface-variant">{L.SECTION_MEDIA}</p>

        {/* Images */}
        <div ref={imagesRef} className="flex flex-col gap-1">
          <p className="text-[16px] font-normal text-[#181A1B]">
            {L.SECTION_IMAGES}
          </p>
          <div className={'max-w-[50%]'}>
            <FileUpload
              onFileSelect={onImagesSelect}
              onError={onImagesError}
              selectedFiles={images}
              allowedTypes={[FileType.JPG, FileType.JPEG, FileType.PNG]}
              maxFiles={2}
              uploadProgress={imageUploadProgress}
              error={Boolean(imagesError)}
              errorMessage={imagesError || undefined}
              disabled={isSubmitting}
              fullWidth
            />
          </div>
        </div>

        {/* Document */}
        <div ref={documentRef} className="flex flex-col gap-1">
          <p className="text-[16px] font-normal text-[#181A1B]">
            {L.SECTION_DOCUMENT}
          </p>
          <div className={'max-w-[50%]'}>
            <FileUpload
              onFileSelect={onDocumentSelect}
              onError={onDocumentError}
              selectedFiles={document ? [document] : []}
              allowedTypes={[
                FileType.JPG,
                FileType.JPEG,
                FileType.PNG,
                FileType.PDF,
                FileType.DOCX,
              ]}
              maxFiles={1}
              uploadProgress={documentUploadProgress}
              error={Boolean(documentError)}
              errorMessage={documentError || undefined}
              disabled={isSubmitting}
              fullWidth
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Step2DetailsAttachments;
